import classNames from 'classnames';
import { Icons as Icon } from '../../Icons/Icons';
import { Button, Wrapper } from 'components';
import { routes } from 'settings/navigation/routes';
import { handleViewForm } from 'utils';

import { Table } from 'components-antd';

import styles from './styles.module.scss';
import {
  mapTasksStatuses,
  tasksStatusesIds,
  timezoneLabels,
} from 'settings/constants/transactionTasks';
import { getTaskAggregateEffect } from 'store/effects/taskAggregate';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import {
  addSpaceBeforeCapitalLetters,
  formatDate,
  getDateOnly,
  isBeforeToday,
  isToday,
  isYesterday,
} from 'helpers';
import { isNumber } from 'lodash-es';
import {
  getTransactionEffect,
  setTransactionTaskParamsEffect,
  setTransactionTasksLastStateEffect,
} from 'store/effects';
import { FORM_CATEGORY } from 'settings/constants/transaction';
import { getPublishedFormEffect } from 'store/effects/formProcess';
import { useHistory } from 'react-router-dom';
import { Task } from 'pages/Workshop/Transactions/TransactionTasks/components';

export const TasksModal = ({ row, variant = '' }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [data, setData] = useState([]);
  const [isPending, setIsPending] = useState(true);

  useEffect(() => {
    const filters: any = {
      assignedToIds: [row?.Id],
      campaignOnly: false,
      statuses: [
        tasksStatusesIds.new,
        tasksStatusesIds.inProgress,
        tasksStatusesIds.stuck,
        tasksStatusesIds.overdue,
        tasksStatusesIds.done,
      ],
    };

    if (variant === 'dueToday') {
      filters.dueFrom = moment.utc().startOf('day').toISOString();
      filters.dueTo = moment.utc().endOf('day').toISOString();
    }

    if (variant === 'stuckTasks') {
      filters.statuses = [tasksStatusesIds.stuck];
    } else if (variant === 'overdueTasks') {
      filters.statuses = [tasksStatusesIds.overdue];
    } else if (variant === 'openTasks') {
      filters.statuses = [
        tasksStatusesIds.new,
        tasksStatusesIds.inProgress,
        tasksStatusesIds.stuck,
        tasksStatusesIds.overdue,
      ];
    } else {
      filters.statuses = [
        tasksStatusesIds.new,
        tasksStatusesIds.inProgress,
        tasksStatusesIds.stuck,
        tasksStatusesIds.overdue,
        tasksStatusesIds.done,
      ];
    }

    dispatch(
      getTaskAggregateEffect(
        {
          filters,
        },
        undefined,
        (err, payload) => {
          if (!err) {
            setData(payload?.data?.result || []);
          }
          setIsPending(false);
        },
      ),
    );
  }, []);

  const handleNewFormTask = (EntityId, transactionId, taskId) => {
    dispatch(
      getPublishedFormEffect({ formId: EntityId }, (err, form) => {
        if (!err) {
          const { data } = form;
          history.push({
            pathname: routes.workshopFormProcessRequest,
            state: {
              formId: data?.value?.FormId,
              versionId: data?.value?.Id,
              propertyTransactionId: transactionId,
              taskId: taskId,
            },
          });
        }
      }),
    );
  };

  const handleFormTask = (formProcessDetails) => {
    if (formProcessDetails) {
      const { url, type } = formProcessDetails;
      if (url) {
        handleViewForm(history, url, type);
      }
    }
  };

  const onClickTask = useCallback(
    (record) => {
      if (record) {
        const {
          TransactionId: transactionId,
          Id: taskId,
          CampaignId: campaignId,
          Category,
          EntityId,
          Status,
          formProcessDetails,
        } = record;

        const category = Category?.Name;

        if (category === FORM_CATEGORY && Status === tasksStatusesIds.new && transactionId) {
          handleNewFormTask(EntityId, transactionId, taskId);
        } else if (category === FORM_CATEGORY) {
          handleFormTask(formProcessDetails);
        } else {
          transactionId &&
            dispatch(
              getTransactionEffect({
                id: transactionId,
                include: 'milestones,participants,tasks,transactioncommission',
              }),
            );
          dispatch(
            setTransactionTaskParamsEffect({
              taskId,
              transactionId,
              campaignId,
              isTaskDetail: true,
            }),
          );
          dispatch(setTransactionTasksLastStateEffect({ taskId }));
        }
      }
    },
    [dispatch, history],
  );

  const detailColumns = [
    {
      title: 'Task',
      key: 'Title',
      dataIndex: 'Title',
      sorter: (a, b) => {
        let name = a?.Title;
        let secondName = b?.Title;
        return name.localeCompare(secondName);
      },
      render: (Title, row) => {
        const address = row?.Transaction?.Property?.Address || {};
        return (
          <div className={classNames(styles.val, styles.task)}>
            <Icon className={styles.icon} variant={Icon.TASK} />
            <div>
              <p className={styles.p1}>{Title}</p>
              <p className={styles.p2}>{address?.Line1}</p>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Status',
      key: 'Status',
      dataIndex: 'Status',
      sorter: (a, b) => {
        if (a.Status < b.Status) return -1;
        if (a.Status > b.Status) return 1;
        return 0;
      },
      render: (Status) => {
        return (
          <div className={classNames(styles.val, styles.status)}>
            <span className={classNames(styles.status, styles[Status])}>
              {addSpaceBeforeCapitalLetters(mapTasksStatuses?.[Status] || Status)}
            </span>
          </div>
        );
      },
    },
    {
      title: 'Due Date',
      key: 'DueDate',
      dataIndex: 'DueDate',
      sorter: (a, b) => moment(a.DueDate).unix() - moment(b.DueDate).unix(),
      render: (_, { DueDate, Hours, Minutes, Timezone }) => {
        let dueDate = DueDate ? getDateOnly(DueDate, 'M/D/YYYY') : 'N/A';
        let isPrimaryDate = false;
        const time = moment({ hour: Hours, minute: Minutes }).toDate();

        if (isToday(dueDate)) {
          dueDate = 'Today';
          isPrimaryDate = true;
        }
        if (isYesterday(dueDate)) {
          dueDate = 'Yesterday';
          isPrimaryDate = true;
        }
        if (isBeforeToday(dueDate)) {
          isPrimaryDate = true;
        }
        return (
          <>
            <p className={classNames(styles.val, styles.dueDate)}>{dueDate}</p>
            {isNumber(Hours) && isNumber(Minutes) && (
              <p className={classNames({ [styles.datePrimary]: isPrimaryDate })}>
                {formatDate(time, 'hh:mm A')}
                &nbsp;{timezoneLabels?.[Timezone] || ''}
              </p>
            )}
          </>
        );
      },
    },
    {
      title: '',
      key: 'view',
      dataIndex: 'view',
      render: (_, record) => {
        return (
          <div className={classNames(styles.val, styles.buttons)}>
            <Button
              isPending={false}
              className={styles.button}
              onClick={() => {
                onClickTask(record);
              }}
              title={'View'}
              disabled={false}
            />
          </div>
        );
      },
    },
  ];

  return (
    <Wrapper isPending={isPending}>
      <Task />
      <Table
        loading={false}
        className={styles.detailsTable}
        bordered
        columns={detailColumns}
        dataSource={data}
        pagination={false}
        showSorterTooltip={false}
      />
    </Wrapper>
  );
};
